/**
 * AsklawPanelPlaceholder — Day-1 right panel teaser.
 *
 * Per founder re-prioritization 2026-05-05:
 *   - News ships Day-1 WITHOUT side-by-side citation linker
 *   - Detail page right side = "Sắp ra mắt" teaser + "Hỏi Asklaw" CTA
 *   - When Mode A citation linker ships S5-late, swap → AsklawLegalPanel
 *
 * Layout still 60/40 desktop, stacked on mobile, so the swap is purely
 * a component-level replacement (no parent layout change).
 */

import { Link } from 'react-router-dom';
import { useLang } from '../../use-lang';
import { resolveHotNews } from '../../i18n/hotnews';

export interface AsklawPanelPlaceholderProps {
  newsId: string;
  headline: string;
}

export function AsklawPanelPlaceholder({ newsId, headline }: AsklawPanelPlaceholderProps) {
  const { lang } = useLang();
  const copy = resolveHotNews(lang);

  const teaserTitle = lang === 'en'
    ? 'Coming soon: Asklaw legal explainer + Article/Clause citations'
    : 'Sắp ra mắt: Asklaw giải thích + trích dẫn Điều/Khoản';

  const teaserBody = lang === 'en'
    ? 'Asklaw will surface the exact Articles and Clauses cited in this story, side-by-side, with confidence scores and a one-click jump to the original legal text.'
    : 'Asklaw sẽ hiển thị đúng Điều/Khoản được nhắc tới trong tin này, đặt cạnh tóm tắt, kèm điểm tin cậy và link mở thẳng văn bản gốc.';

  const askPrefill = encodeURIComponent(
    copy.ask_prefill_hint.replace('{headline}', headline),
  );

  return (
    <aside
      className="asklaw-panel-placeholder"
      aria-label={teaserTitle}
      style={panelStyle}
    >
      <div style={badgeStyle}>
        <SparkleIcon />
        {lang === 'en' ? 'Coming soon' : 'Sắp ra mắt'}
      </div>

      <h2 style={titleStyle}>{teaserTitle}</h2>

      <p style={bodyStyle}>{teaserBody}</p>

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Link
          to={`/chat?q=${askPrefill}&context_news=${newsId}`}
          className="cta-primary"
          style={{ justifyContent: 'center' }}
          aria-label={copy.ask_cta_aria}
        >
          {copy.ask_cta_detail}
          <span className="arrow" aria-hidden>{'->'}</span>
        </Link>
        <p style={{ fontSize: 12, color: 'var(--asklaw-muted)', margin: 0, textAlign: 'center' }}>
          {lang === 'en'
            ? 'Asklaw chat is live — try asking about this story now.'
            : 'Asklaw chat đã hoạt động — bạn có thể hỏi về tin này ngay.'}
        </p>
      </div>
    </aside>
  );
}

function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M7 1v3M7 10v3M1 7h3M10 7h3M2.5 2.5l2 2M9.5 9.5l2 2M2.5 11.5l2-2M9.5 4.5l2-2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const panelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: 24,
  background:
    'linear-gradient(160deg, color-mix(in srgb, var(--asklaw-orange-soft) 24%, var(--asklaw-paper)) 0%, var(--asklaw-paper) 60%)',
  border: '1px solid color-mix(in srgb, var(--asklaw-orange) 18%, var(--asklaw-border))',
  borderRadius: 14,
  minHeight: 320,
  boxShadow: 'var(--shadow-card)',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  alignSelf: 'flex-start',
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontFamily: 'var(--font-mono)',
  background: 'var(--asklaw-orange)',
  color: '#fff',
  borderRadius: 999,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'clamp(18px, 2.6vw, 22px)',
  fontWeight: 700,
  lineHeight: 1.3,
  color: 'var(--asklaw-ink)',
  letterSpacing: '-0.01em',
};

const bodyStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--asklaw-ink-soft)',
};
